import { useEffect, useRef, useState } from "react";
import {
  Form,
  Typography,
  message,
  Modal,
} from "antd";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { WizardStep } from "../../BookingWizard/WizardStep";
import { useBookingWizard } from "../../../contexts/BookingWizardContext";
import { useBookingWizardV2 } from "../context/BookingWizardV2Context";
import { useTransportTypes } from "../../../hooks/useTransportTypes";
import { useBookingTypes } from "../../../hooks/useBookingTypes";
import { useIncoterms } from "../../../hooks/useIncoterms";
import { useCompany } from "../../../hooks/useCompany";
import {
  useCompanySuppliers,
  type CompanySupplier,
  type CompanySupplierInput,
} from "../../../hooks/useCompanySuppliers";
import { supabase } from "../../../lib/supabase";
import {
  ensureDraftBooking,
  markV2Booking,
} from "../utils/ensureDraftBooking";
import { isMissingField, mapAnalysisToBooking, computeMissingBookingFields } from "../utils/mapAnalysisToBooking";
import { normalizeSupplierEmail } from "../utils/smartDetailsFormRules";
import { SmartDetailsForm } from "../components/SmartDetailsForm";
import type { SupplierFormValues } from "../components/SavedSupplierPicker";
import {
  isFclBookingType,
  isServiceLevel,
  type ServiceLevel,
} from "../../../utils/serviceLevelUtils";

const { Title, Text } = Typography;

interface SmartDetailsFormValues extends SupplierFormValues {
  transport_type: number;
  booking_type?: number;
  service_level?: ServiceLevel;
  booking_incoterm: number;
  origin_pickup_date: dayjs.Dayjs;
  reference?: string;
  saveSupplierForLater?: boolean;
}

export const SmartDetailsStep = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const [form] = Form.useForm<SmartDetailsFormValues>();
  const { state, updateStep } = useBookingWizard();
  const { setV2Step, missingFields, setMissingFields, analysisResult } =
    useBookingWizardV2();
  const { transportTypes, loading: loadingTransport } = useTransportTypes();
  const { bookingTypes, loading: loadingBookingTypes } = useBookingTypes();
  const { incoterms, loading: loadingIncoterms } = useIncoterms();
  const { selectedCompany } = useCompany();
  const { saveCompanySupplier, updateCompanySupplier } =
    useCompanySuppliers(selectedCompany);

  const [products, setProducts] = useState(state.products ?? []);
  const [selectedTransportType, setSelectedTransportType] = useState<
    number | undefined
  >(state.transport_type);
  const [selectedCompanySupplier, setSelectedCompanySupplier] =
    useState<CompanySupplier | null>(null);
  const [saving, setSaving] = useState(false);
  const analysisAppliedRef = useRef(false);
  const fromProductPage = location.state?.fromProductPage;

  useEffect(() => {
    if (fromProductPage && location.state?.previousProducts) {
      setProducts(location.state.previousProducts);
    }
  }, [fromProductPage, location.state?.previousProducts]);

  useEffect(() => {
    form.setFieldsValue({
      transport_type: state.transport_type,
      booking_type: state.booking_type ?? undefined,
      service_level:
        state.service_level && isServiceLevel(state.service_level)
          ? state.service_level
          : undefined,
      booking_incoterm: state.booking_incoterm,
      origin_pickup_date: state.origin_pickup_date
        ? dayjs(state.origin_pickup_date)
        : undefined,
      reference: state.reference,
    });
    if (!fromProductPage) {
      setProducts(state.products ?? []);
    }
    setSelectedTransportType(state.transport_type);
  }, [
    form,
    fromProductPage,
    state.transport_type,
    state.booking_type,
    state.service_level,
    state.booking_incoterm,
    state.origin_pickup_date,
    state.reference,
    state.products,
  ]);

  useEffect(() => {
    const loadSupplier = async () => {
      if (state.supplier_id) {
        const { data } = await supabase
          .from("booking_supplier")
          .select("*")
          .eq("id", state.supplier_id)
          .single();

        if (data) {
          form.setFieldsValue({
            supplier_name: data.name,
            supplier_email: normalizeSupplierEmail(data.email),
            supplier_phone: data.phone,
            supplier_wechat: data.wechat ?? undefined,
            supplier_country: data.country,
            supplier_province: data.province,
            supplier_city: data.city,
            supplier_address: data.address,
          });
        }
        return;
      }

      if (
        analysisResult &&
        incoterms.length &&
        transportTypes.length &&
        !analysisAppliedRef.current
      ) {
        analysisAppliedRef.current = true;
        const mapped = mapAnalysisToBooking(
          analysisResult,
          incoterms,
          transportTypes
        );
        form.setFieldsValue({
          supplier_name: mapped.supplier.name,
          supplier_email: normalizeSupplierEmail(mapped.supplier.email),
          supplier_phone: mapped.supplier.phone,
          supplier_wechat: mapped.supplier.wechat,
          supplier_country: mapped.supplier.country,
          supplier_province: mapped.supplier.province,
          supplier_city: mapped.supplier.city,
          supplier_address: mapped.supplier.address,
          reference: mapped.reference ?? state.reference,
          transport_type: mapped.transport.transport_type ?? state.transport_type,
          booking_incoterm:
            mapped.transport.booking_incoterm ?? state.booking_incoterm,
        });
        if (mapped.products.length > 0 && (state.products?.length ?? 0) === 0) {
          setProducts(mapped.products);
        }
        setMissingFields(
          computeMissingBookingFields({
            ...form.getFieldsValue(true),
            products:
              mapped.products.length > 0 && (state.products?.length ?? 0) === 0
                ? mapped.products
                : (state.products ?? []),
          })
        );
      }
    };

    loadSupplier();
  }, [
    analysisResult,
    form,
    incoterms,
    setMissingFields,
    state.booking_incoterm,
    state.products,
    state.reference,
    state.supplier_id,
    state.transport_type,
    transportTypes,
  ]);

  const handleSupplierSelect = (
    values: SupplierFormValues,
    supplier: CompanySupplier | null
  ) => {
    setSelectedCompanySupplier(supplier);
    form.setFieldsValue(values);
  };

  const handleAddProduct = (
    product: NonNullable<typeof products>[number]
  ) => {
    setProducts((prev) => [...prev, product]);
  };

  const isSeafreight = selectedTransportType === 2;

  const recomputeMissing = (values: SmartDetailsFormValues) => {
    const next = computeMissingBookingFields({ ...values, products });
    setMissingFields(next);
    return next;
  };

  useEffect(() => {
    const values = form.getFieldsValue(true) as SmartDetailsFormValues;
    recomputeMissing(values);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- recompute when transport/products change
  }, [products, selectedTransportType, state.transport_type, state.booking_incoterm]);

  const handleFormValuesChange = () => {
    recomputeMissing(form.getFieldsValue(true) as SmartDetailsFormValues);
  };

  const fieldStatus = (key: string) =>
    isMissingField(missingFields, key) ? ("warning" as const) : undefined;

  const formProps = {
    missingFields,
    isSeafreight,
    products,
    loadingTransport,
    loadingBookingTypes,
    loadingIncoterms,
    transportTypes,
    bookingTypes,
    incoterms,
    onTransportChange: (value: number) => {
      setSelectedTransportType(value);
      if (value !== 2) {
        form.setFieldValue("booking_type", undefined);
        form.setFieldValue("service_level", undefined);
      }
    },
    onSupplierSelect: handleSupplierSelect,
    onAddProduct: handleAddProduct,
    onProductsChange: setProducts,
    fieldStatus,
  };

  const saveProducts = async (bookingId: number, companyId: number) => {
    const saved = [];
    for (const product of products) {
      const productData = {
        booking_id: bookingId,
        name: product.name,
        hs_code: product.hs_code,
        gross_weight: product.gross_weight,
        cbm: product.cbm,
        value: product.value,
        pieces: product.pieces,
        cartons: product.cartons,
        ean:
          product.ean && product.ean.toString().trim() !== ""
            ? product.ean
            : null,
        company_id: companyId,
      };

      if (!product.id) {
        const { data, error } = await supabase
          .from("booking_product")
          .insert(productData)
          .select()
          .single();
        if (error) throw error;
        saved.push({ ...product, id: data.id });
      } else {
        const { error } = await supabase
          .from("booking_product")
          .update(productData)
          .eq("id", product.id);
        if (error) throw error;
        saved.push(product);
      }
    }
    return saved;
  };

  const handleNext = async () => {
    try {
      const values = await form.validateFields();

      if (products.length === 0) {
        Modal.error({
          title: t("bookingWizard.productDetails.noProductsTitle"),
          content: t("bookingWizard.productDetails.noProductsContent"),
        });
        return;
      }

      const remaining = recomputeMissing(values);
      if (remaining.length > 0) {
        message.warning(t("bookingWizardV2.details.fillHighlighted"));
      }

      setSaving(true);
      const bookingId = await ensureDraftBooking(selectedCompany, state.id);
      markV2Booking(bookingId);

      if (values.saveSupplierForLater && selectedCompany) {
        const companySupplierData: CompanySupplierInput = {
          name: values.supplier_name,
          email: values.supplier_email,
          phone: values.supplier_phone,
          wechat: values.supplier_wechat,
          country: values.supplier_country,
          province: values.supplier_province,
          city: values.supplier_city,
          address: values.supplier_address,
          company_id: selectedCompany,
        };

        if (selectedCompanySupplier) {
          await updateCompanySupplier(
            selectedCompanySupplier.id,
            companySupplierData
          );
        } else {
          await saveCompanySupplier(companySupplierData);
        }
      }

      let supplierId = state.supplier_id;
      const supplierData = {
        company_id: selectedCompany!,
        name: values.supplier_name,
        email: values.supplier_email,
        phone: values.supplier_phone,
        wechat: values.supplier_wechat,
        country: values.supplier_country,
        province: values.supplier_province,
        city: values.supplier_city,
        address: values.supplier_address,
      };

      if (supplierId) {
        await supabase
          .from("booking_supplier")
          .update(supplierData)
          .eq("id", supplierId);
      } else {
        const { data, error } = await supabase
          .from("booking_supplier")
          .insert(supplierData)
          .select()
          .single();
        if (error) throw error;
        supplierId = data.id;
      }

      const savedProducts = await saveProducts(bookingId, selectedCompany!);

      const transportUpdate = {
        transport_type: values.transport_type,
        booking_type: values.transport_type === 2 ? values.booking_type : null,
        service_level:
          values.transport_type === 2 && isFclBookingType(values.booking_type)
            ? values.service_level ?? null
            : null,
        booking_incoterm: values.booking_incoterm,
        origin_pickup_date: values.origin_pickup_date.toISOString(),
        reference: values.reference,
        supplier_id: supplierId,
        products: savedProducts,
        includeExportClearanceLicense:
          values.booking_incoterm === 3 ? false : state.includeExportClearanceLicense,
      };

      await updateStep(4, transportUpdate);
      setV2Step(2);
    } catch (error) {
      console.error("SmartDetailsStep save failed:", error);
      message.error(t("bookingWizardV2.details.saveFailed"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <WizardStep
      onNext={handleNext}
      onPrevious={() => setV2Step(0)}
      nextDisabled={saving}
    >
      <div style={{ width: "100%", minWidth: 0 }}>
        <div style={{ textAlign: "center", marginBottom: 16 }}>
          <Title level={3}>{t("bookingWizardV2.details.title")}</Title>
          <Text type="secondary">{t("bookingWizardV2.details.subtitle")}</Text>
        </div>

        <Form form={form} layout="vertical" onValuesChange={handleFormValuesChange}>
          <SmartDetailsForm {...formProps} />
        </Form>
      </div>
    </WizardStep>
  );
};
